import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Send, Download, Edit2, Trash2, Plus, Minus, Check, Save, Eye, Clock, AlertCircle } from 'lucide-react';
import '../styles/quote-modal.css';
import { deleteQuote, getGymPlansList, getGymAddonsList, sendQuote, resendQuote, getQuote, editQuote, saveQuoteDraft, downloadQuoteUrl } from '../../services/apiServices';
import { Await } from 'react-router';

const QuoteModal = ({ lead, gymId, onClose, onSent, onSave }) => {
    const [catalogue, setCatalogue] = useState([]);
    const [search, setSearch] = useState('');
    const [apiError, setApiError] = useState(false);
    const [existingQuote, setExistingQuote] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [items, setItems] = useState([]);
    const [message, setMessage] = useState('');
    const [discount, setDiscount] = useState(0);
    const [taxRate] = useState(18);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fetchingQuote, setFetchingQuote] = useState(false);
    const [quotesList, setQuotesList] = useState([]);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const staffname = JSON.parse(localStorage.getItem("loggedUserInfo")).name;
    const staffid = JSON.parse(localStorage.getItem("loggedUserInfo"))._id;
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setApiError(false);
            try {
                const gymid = gymId || JSON.parse(localStorage.getItem('loggedUserInfo'))?.gymid;
                if (!gymid) { setLoading(false); return; }

                const plansRespo = await getGymPlansList(gymid);
                let plansRaw = [];
                if (plansRespo && plansRespo !== "false") plansRaw = await plansRespo.json();
                else if (plansRespo === "false") setApiError(true);

                const planEntries = [];
                if (Array.isArray(plansRaw)) {
                    plansRaw.forEach(plan => {
                        if (plan.subplans && plan.subplans.length > 0) {
                            plan.subplans.forEach(sub => {
                                planEntries.push({
                                    _id: sub._id,
                                    displayName: `${plan.name} - ${sub.name}`,
                                    price: Number(sub.price) || 0,
                                    numberofdays: sub.numberofdays,
                                    category: 'Membership Plans',
                                    type: 'plan',
                                });
                            });
                        } else if (!plan.baseplan) {
                            planEntries.push({
                                _id: plan._id,
                                displayName: plan.name,
                                price: Number(plan.price) || 0,
                                numberofdays: plan.numberofdays,
                                category: 'Membership Plans',
                                type: 'plan',
                            });
                        }
                    });
                }

                const addonsRespo = await getGymAddonsList(gymid);
                let addonsRaw = [];
                if (addonsRespo && addonsRespo !== "false") addonsRaw = await addonsRespo.json();

                const addonEntries = (Array.isArray(addonsRaw) ? addonsRaw : []).map(addon => ({
                    _id: addon._id,
                    displayName: addon.name,
                    price: Number(addon.price || addon.amount) || 0,
                    category: 'Add-ons & Extras',
                    type: 'addon',
                }));

                setCatalogue([...planEntries, ...addonEntries]);
                await fetchQuotesList(lead._id, gymid);
            } catch (err) {
                console.error('QuoteModal init:', err);
                setApiError(true);
            } finally {
                setLoading(false);
            }
        };
        if (lead?._id) init();
    }, [lead?._id, gymId]);

    const fetchQuotesList = async (leadId) => {
        try {
            const response = await getQuote(leadId);
            if (response && response !== "false") {
                const data = await response.json();
                if (data?.success && data?.data) {
                    setQuotesList(Array.isArray(data.data) ? data.data : [data.data]);
                }
            }
        } catch (error) {
            console.log('No quotes found');
        }
    };

    const loadQuote = (quote) => {
        setSelectedQuote(quote);
        setItems(quote.items || []);
        setMessage(quote.message || '');
        setDiscount(quote.discount || 0);
        setViewMode('view');
    };

    const filtered = useMemo(() => {
        if (!search) return catalogue;
        const q = search.toLowerCase();
        return catalogue.filter(p => p.displayName?.toLowerCase().includes(q));
    }, [catalogue, search]);

    const grouped = useMemo(() => {
        const g = {};
        filtered.forEach(p => {
            if (!g[p.category]) g[p.category] = [];
            g[p.category].push(p);
        });
        return g;
    }, [filtered]);

    const isSelected = (id) => items.some(i => i.id === id || i._id === id);

    const toggleItem = (plan) => {
        const key = plan._id || plan.id;
        if (isSelected(key)) {
            setItems(p => p.filter(i => (i.id || i._id) !== key));
        } else {
            setItems(p => [...p, { id: key, _id: key, name: plan.displayName || plan.name, price: plan.price || Number(plan.amount) || 0, qty: 1, category: plan.category }]);
        }
    };

    const updateQty = (id, delta) => setItems(p => p.map(i => (i.id || i._id) === id ? { ...i, qty: Math.max(1, (i.qty || 1) + delta) } : i));
    const removeItem = (id) => setItems(p => p.filter(i => (i.id || i._id) !== id));

    const subtotal = items.reduce((s, i) => s + (i.price * (i.qty || 1)), 0);
    const discountAmt = Math.round((subtotal * discount) / 100);
    const taxAmt = Math.round(((subtotal - discountAmt) * taxRate) / 100);
    const total = subtotal - discountAmt + taxAmt;
    const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

    const handleSaveDraft = async () => {
        if (!items.length) return;
        setSaving(true);
        try {
            const gymid = gymId || JSON.parse(localStorage.getItem('loggedUserInfo'))?.gymid;
            const payload = { leadId: lead._id, gymId: gymid, items: items.map(item => ({ id: item.id || item._id, name: item.name, price: item.price, qty: item.qty, category: item.category })), message, discount, taxRate, subtotal, total };
            const response = await saveQuoteDraft(payload);
            if (response && response !== "false") {
                const data = await response.json();
                if (data?.success) {
                    onSave?.(data.data);
                    await fetchQuotesList(lead._id);
                    setViewMode('list');
                }
            }
        } catch (err) { console.error(err); }
        finally { setSaving(false); }
    };

    const handleSend = async () => {
        if (!items.length) return;
        setSending(true);
        try {
            const gymid = gymId || JSON.parse(localStorage.getItem('loggedUserInfo'))?.gymid;
            const payload = {
                staffName: staffname,
                staffId: staffid, leadId: lead._id, gymId: gymid, items: items.map(item => ({ id: item.id || item._id, name: item.name, price: item.price, qty: item.qty, category: item.category })), message, discount, taxRate, subtotal, total
            };
            const response = await sendQuote(payload);
            if (response && response !== "false") {
                const data = await response.json();
                if (data?.success) {
                    setExistingQuote(data.data);
                    setViewMode('view');
                    onSent?.(data.data);
                    await fetchQuotesList(lead._id);
                }
            }
        } catch (err) { console.error(err); }
        finally { setSending(false); }
    };

    const handleResend = async () => {
        if (!selectedQuote) return;
        setSending(true);
        try {
            const response = await resendQuote(selectedQuote._id, {
                message: selectedQuote.message, staffName: staffname,
                staffId: staffid,
            });
            if (response && response !== "false") { const data = await response.json(); }
        } catch (err) { console.error(err); }
        finally { setSending(false); }
    };

    const handleUpdate = async () => {
        if (!selectedQuote || !items.length) return;
        setSending(true);
        try {
            const payload = { items: items.map(item => ({ id: item.id || item._id, name: item.name, price: item.price, qty: item.qty, category: item.category })), message, discount, taxRate, subtotal, total };
            const response = await editQuote(selectedQuote._id, payload);
            if (response && response !== "false") {
                const data = await response.json();
                if (data?.success) { setSelectedQuote(data.data); setViewMode('view'); await fetchQuotesList(lead._id); }
            }
        } catch (err) { console.error(err); }
        finally { setSending(false); }
    };

    const handleDelete = async () => {
        setShowDeleteConfirm(false);
        await deleteQuote(selectedQuote._id)
        await fetchQuotesList(lead._id);
        setViewMode('list');
    };

    const handleDownload = async (quote) => {
        try {
            const response = await downloadQuoteUrl(quote._id, { gymid: quote.gymId, quoteid: quote._id });
            const data = await response.json();
            if (data.url) { const a = document.createElement('a'); a.href = data.url; a.setAttribute('download', 'quote.pdf'); document.body.appendChild(a); a.click(); document.body.removeChild(a); }
        } catch (err) { console.error(err); }
    };

    const startNewQuote = () => { setItems([]); setMessage(''); setDiscount(0); setSelectedQuote(null); setViewMode('create'); };

    if (loading || fetchingQuote) return (
        <div className="qm-overlay">
            <div className="qm-modal qm-modal-center"><div className="qm-spinner" /></div>
        </div>
    );

    if (apiError) return (
        <div className="qm-overlay">
            <div className="qm-modal" style={{ padding: '32px', textAlign: 'center', maxWidth: '320px' }}>
                <p style={{ marginBottom: '16px', color: '#64748b' }}>Failed to load data. Please try again.</p>
                <button className="qm-btn-cancel" onClick={onClose}>Close</button>
            </div>
        </div>
    );

    // ── LIST mode ─────────────────────────────────────────────────────────
    if (viewMode === 'list') return (
        <div className="qm-overlay" onClick={e => e.target.classList.contains('qm-overlay') && onClose()}>
            <div className="qm-modal qm-list-modal">

                {/* Header */}
                <div className="qm-list-header">
                    <div>
                        <div className="qm-title">Quotes for {lead.name}</div>
                        <div className="qm-list-sub">{lead.phone}{lead.email ? ` · ${lead.email}` : ''}</div>
                    </div>
                    <div className="qm-list-header-right">
                        <button className="qm-new-quote-btn" onClick={startNewQuote}>
                            <Plus size={14} /> New Quote
                        </button>
                        <button className="qm-close" onClick={onClose}><X size={18} /></button>
                    </div>
                </div>

                {/* Body */}
                <div className="qm-list-body">
                    {quotesList.length === 0 ? (
                        <div className="qm-empty-list">
                            <AlertCircle size={36} />
                            <p>No quotes yet for this lead</p>
                            <button className="qm-new-quote-btn qm-new-quote-sm" onClick={startNewQuote}>
                                <Plus size={14} /> Create First Quote
                            </button>
                        </div>
                    ) : (
                        <table className="qm-table">
                            <thead>
                                <tr>
                                    <th className="qm-th">Quote ID</th>
                                    <th className="qm-th">Date</th>
                                    <th className="qm-th">Items</th>
                                    <th className="qm-th qm-th-right">Total</th>
                                    <th className="qm-th">Status</th>
                                    <th className="qm-th qm-th-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quotesList.map((quote, index) => {
                                    const names = quote.items?.slice(0, 2).map(i => i.name).join(', ')
                                        + (quote.items?.length > 2 ? ` +${quote.items.length - 2}` : '');
                                    return (
                                        <tr key={quote._id || index} className="qm-tr">
                                            <td className="qm-td qm-td-id">
                                                #{quote.displayId || quote._id?.slice(-6)}
                                            </td>
                                            <td className="qm-td qm-td-date">
                                                {quote.createdAt ? new Date(quote.createdAt).toLocaleDateString('en-GB') : '—'}
                                            </td>
                                            <td className="qm-td qm-td-items" title={quote.items?.map(i => i.name).join(', ')}>
                                                {names || '—'}
                                            </td>
                                            <td className="qm-td qm-td-total">
                                                {fmt(quote.total || 0)}
                                            </td>
                                            <td className="qm-td">
                                                <span className={`qm-status-pill status-${quote.status || 'draft'}`}>
                                                    {quote.status || 'Draft'}
                                                </span>
                                            </td>
                                            <td className="qm-td qm-td-actions">
                                                <button className="qm-action-btn" onClick={() => loadQuote(quote)} title="View"><Eye size={15} /></button>
                                                <button className="qm-action-btn" onClick={() => { setSelectedQuote(quote); setItems(quote.items || []); setMessage(quote.message || ''); setDiscount(quote.discount || 0); setViewMode('edit'); }} title="Edit"><Edit2 size={15} /></button>
                                                {quote.status === 'sent' && (
                                                    <button className="qm-action-btn" onClick={() => { setSelectedQuote(quote); handleResend(); }} title="Resend" disabled={sending}><Send size={15} /></button>
                                                )}
                                                <button className="qm-action-btn" onClick={() => handleDownload(quote)} title="Download"><Download size={15} /></button>
                                                <button className="qm-action-btn qm-action-delete" onClick={() => { setSelectedQuote(quote); setShowDeleteConfirm(true); }} title="Delete"><Trash2 size={15} /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Delete confirm */}
                {showDeleteConfirm && (
                    <div className="qm-confirm-overlay">
                        <div className="qm-confirm-box">
                            <p>Delete this quote? This cannot be undone.</p>
                            <div className="qm-confirm-actions">
                                <button className="qm-btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                                <button className="qm-btn-danger" onClick={handleDelete}>Delete</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    // ── VIEW mode ─────────────────────────────────────────────────────────
    if (viewMode === 'view' && selectedQuote) return (
        <div className="qm-overlay" onClick={e => e.target.classList.contains('qm-overlay') && onClose()}>
            <div className="qm-modal qm-view-modal">
                <div className="qm-view-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span className="qm-view-title">Quote #{selectedQuote.displayId || selectedQuote._id?.slice(-6)}</span>
                        <span className={`qm-status-pill status-${selectedQuote.status || 'draft'}`}>{selectedQuote.status || 'Draft'}</span>
                    </div>
                    <button className="qm-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="qm-view-body">
                    <div className="qm-view-contact">
                        <div className="qm-view-name">{lead.name}</div>
                        <div className="qm-view-sub">{lead.phone}</div>
                        {lead.email && <div className="qm-view-sub">{lead.email}</div>}
                    </div>
                    <div className="qm-view-divider" />
                    <div className="qm-view-section-label">Items</div>
                    {items.map((item, i) => (
                        <div key={i} className="qm-view-item">
                            <div>
                                <div className="qm-view-item-name">{item.name}</div>
                                <div className="qm-view-item-sub">₹{item.price?.toLocaleString('en-IN')} × {item.qty}</div>
                            </div>
                            <div className="qm-view-item-price">{fmt(item.price * item.qty)}</div>
                        </div>
                    ))}
                    {message && <><div className="qm-view-section-label" style={{ marginTop: 12 }}>Message</div><div className="qm-view-message">{message}</div></>}
                    <div className="qm-view-totals">
                        <div className="qm-view-total-line"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                        {discount > 0 && <div className="qm-view-total-line"><span>Discount ({discount}%)</span><span>-{fmt(discountAmt)}</span></div>}
                        <div className="qm-view-total-line"><span>Tax ({taxRate}%)</span><span>{fmt(taxAmt)}</span></div>
                        <div className="qm-view-total-final"><span>Total Amount</span><span className="qm-view-total-value">{fmt(total)}</span></div>
                    </div>
                </div>
                <div className="qm-view-footer">
                    <button className="qm-btn-outline" onClick={() => setViewMode('list')}>← Back</button>
                    <button className="qm-btn-outline" onClick={() => handleDownload(selectedQuote)}><Download size={15} /> Download</button>
                    <button className="qm-btn-outline" onClick={() => { setViewMode('edit'); }}><Edit2 size={15} /> Edit</button>
                    {selectedQuote?.status === 'sent' && (
                        <button className="qm-btn-primary" onClick={handleResend} disabled={sending}><Send size={15} /> {sending ? 'Sending…' : 'Resend'}</button>
                    )}
                </div>
            </div>
        </div>
    );

    // ── CREATE / EDIT mode ────────────────────────────────────────────────
    const isEditMode = viewMode === 'edit';
    return (
        <div className="qm-overlay" onClick={e => e.target.classList.contains('qm-overlay') && onClose()}>
            <div className="qm-modal qm-create-modal">
                <div className="qm-header">
                    <span className="qm-title">{isEditMode ? 'Edit Quote' : 'Create Quote'} for {lead.name}</span>
                    <button className="qm-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="qm-body">
                    <div className="qm-left">
                        <div className="qm-search-wrap">
                            <Search size={15} className="qm-search-icon" />
                            <input className="qm-search" placeholder="Search Plans, PT Sessions, Addons..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <div className="qm-catalogue">
                            {Object.entries(grouped).map(([cat, plans]) => (
                                <div key={cat} className="qm-cat-section">
                                    <div className="qm-cat-label">{cat}</div>
                                    <div className="qm-plan-grid">
                                        {plans.map(plan => {
                                            const key = plan._id || plan.id;
                                            const sel = isSelected(key);
                                            return (
                                                <button key={key} className={`qm-plan-card ${sel ? 'qm-plan-selected' : ''}`} onClick={() => toggleItem(plan)}>
                                                    {sel && <span className="qm-plan-check"><Check size={11} /></span>}
                                                    <div className="qm-plan-name">{plan.displayName}</div>
                                                    <div className="qm-plan-price">₹{(plan.price || 0).toLocaleString('en-IN')}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            {Object.keys(grouped).length === 0 && <div className="qm-empty">No plans found</div>}
                        </div>
                    </div>
                    <div className="qm-right">
                        <div className="qm-summary-title">Quote Summary</div>
                        <div className="qm-summary-contact">
                            <div>{lead.name}</div><div>{lead.phone}</div>
                            {lead.email && <div>{lead.email}</div>}
                        </div>
                        <div className="qm-summary-items">
                            {items.length === 0 && <div className="qm-summary-empty">Click a plan to add it</div>}
                            {items.map(item => {
                                const key = item.id || item._id;
                                return (
                                    <div key={key} className="qm-summary-item">
                                        <div className="qm-si-top">
                                            <span className="qm-si-name">{item.name}</span>
                                            <button className="qm-si-remove" onClick={() => removeItem(key)}><Trash2 size={14} /></button>
                                        </div>
                                        <div className="qm-si-sub">₹{item.price?.toLocaleString('en-IN')} × {item.qty}</div>
                                        <div className="qm-si-bottom">
                                            <div className="qm-qty-row">
                                                <span className="qm-qty-label">Qty:</span>
                                                <button className="qm-qty-btn" onClick={() => updateQty(key, -1)}><Minus size={12} /></button>
                                                <span className="qm-qty-val">{item.qty}</span>
                                                <button className="qm-qty-btn" onClick={() => updateQty(key, 1)}><Plus size={12} /></button>
                                            </div>
                                            <span className="qm-si-price">{fmt(item.price * item.qty)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {items.length > 0 && (
                            <div className="qm-message-wrap">
                                <div className="qm-message-label">Personalized Message</div>
                                <textarea className="qm-message-input" placeholder="Add a personal note..." value={message} onChange={e => setMessage(e.target.value)} rows={3} />
                            </div>
                        )}
                        {items.length > 0 && (
                            <div className="qm-totals">
                                <div className="qm-total-row"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                                <div className="qm-total-row">
                                    <span>Discount</span>
                                    <div className="qm-discount-wrap">
                                        <input className="qm-discount-input" type="number" min="0" max="100" value={discount} onChange={e => setDiscount(Number(e.target.value))} />
                                        <span className="qm-discount-pct">%</span>
                                        <span>{discount > 0 ? `-${fmt(discountAmt)}` : '₹0'}</span>
                                    </div>
                                </div>
                                <div className="qm-total-row"><span>Tax ({taxRate}%)</span><span>{fmt(taxAmt)}</span></div>
                                <div className="qm-total-final"><span>Total</span><span>{fmt(total)}</span></div>
                            </div>
                        )}
                        <div className="qm-footer">
                            <button className="qm-btn-cancel" onClick={() => setViewMode('list')}>Cancel</button>
                            {items.length > 0 && <>
                                <button className="qm-btn-outline" onClick={handleSaveDraft} disabled={saving || sending}><Save size={15} /> {saving ? 'Saving…' : 'Save Draft'}</button>
                                {isEditMode
                                    ? <button className="qm-btn-primary" onClick={handleUpdate} disabled={sending}><Edit2 size={15} /> {sending ? 'Updating…' : 'Update & Send'}</button>
                                    : <button className="qm-btn-primary" onClick={handleSend} disabled={sending}><Send size={15} /> {sending ? 'Sending…' : 'Send Quote'}</button>
                                }
                            </>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuoteModal;